import { getCountryCodeFromName } from './countryMapping';

export interface CountryTimezoneData {
  name: string;
  flag: string;
  countryCode?: string;
  timezone: string;
  abbreviation: string;
}

export interface StateTimezoneData extends CountryTimezoneData {
  stateCode?: string;
  country: string;
}

// Helper function to create country timezone data with ISO codes
const createCountryData = (name: string, timezone: string, abbreviation: string): CountryTimezoneData => ({
  name,
  flag: '🌐', // Fallback emoji, will be replaced by SVG
  countryCode: getCountryCodeFromName(name),
  timezone,
  abbreviation
});

// Country name to timezone mapping
export const COUNTRY_TIMEZONE_MAP: Record<string, CountryTimezoneData> = {
  // North America
  'United States': createCountryData('United States', 'America/New_York', 'EST'),
  'USA': createCountryData('United States', 'America/New_York', 'EST'),
  'US': createCountryData('United States', 'America/New_York', 'EST'),
  'Canada': createCountryData('Canada', 'America/Toronto', 'EST'),
  
  // Europe
  'United Kingdom': createCountryData('United Kingdom', 'Europe/London', 'GMT'),
  'UK': createCountryData('United Kingdom', 'Europe/London', 'GMT'),
  'Germany': createCountryData('Germany', 'Europe/Berlin', 'CET'),
  'France': createCountryData('France', 'Europe/Paris', 'CET'),
  'Spain': createCountryData('Spain', 'Europe/Madrid', 'CET'),
  'Italy': createCountryData('Italy', 'Europe/Rome', 'CET'),
  'Netherlands': createCountryData('Netherlands', 'Europe/Amsterdam', 'CET'),
  'Switzerland': createCountryData('Switzerland', 'Europe/Zurich', 'CET'),
  'Austria': createCountryData('Austria', 'Europe/Vienna', 'CET'),
  'Belgium': createCountryData('Belgium', 'Europe/Brussels', 'CET'),
  'Norway': createCountryData('Norway', 'Europe/Oslo', 'CET'),
  'Sweden': createCountryData('Sweden', 'Europe/Stockholm', 'CET'),
  'Denmark': createCountryData('Denmark', 'Europe/Copenhagen', 'CET'),
  'Finland': createCountryData('Finland', 'Europe/Helsinki', 'EET'),
  'Poland': createCountryData('Poland', 'Europe/Warsaw', 'CET'),
  'Czech Republic': createCountryData('Czech Republic', 'Europe/Prague', 'CET'),
  'Hungary': createCountryData('Hungary', 'Europe/Budapest', 'CET'),
  'Portugal': createCountryData('Portugal', 'Europe/Lisbon', 'WET'),
  'Ireland': createCountryData('Ireland', 'Europe/Dublin', 'GMT'),
  'Greece': createCountryData('Greece', 'Europe/Athens', 'EET'),
  
  // Asia
  'China': createCountryData('China', 'Asia/Shanghai', 'CST'),
  'Japan': createCountryData('Japan', 'Asia/Tokyo', 'JST'),
  'India': createCountryData('India', 'Asia/Kolkata', 'IST'),
  'Singapore': createCountryData('Singapore', 'Asia/Singapore', 'SGT'),
  'South Korea': createCountryData('South Korea', 'Asia/Seoul', 'KST'),
  'Bangladesh': createCountryData('Bangladesh', 'Asia/Dhaka', 'BST'),
  'Thailand': createCountryData('Thailand', 'Asia/Bangkok', 'ICT'),
  'Malaysia': createCountryData('Malaysia', 'Asia/Kuala_Lumpur', 'MYT'),
  
  // Oceania
  'Australia': createCountryData('Australia', 'Australia/Sydney', 'AEST'),
  'New Zealand': createCountryData('New Zealand', 'Pacific/Auckland', 'NZST'),
  
  // Middle East
  'UAE': createCountryData('UAE', 'Asia/Dubai', 'GST'),
  'Saudi Arabia': createCountryData('Saudi Arabia', 'Asia/Riyadh', 'AST'),
  
  // South America
  'Brazil': createCountryData('Brazil', 'America/Sao_Paulo', 'BRT'),
  'Argentina': createCountryData('Argentina', 'America/Argentina/Buenos_Aires', 'ART'),
  
  // Africa
  'South Africa': createCountryData('South Africa', 'Africa/Johannesburg', 'SAST'),
  'Nigeria': createCountryData('Nigeria', 'Africa/Lagos', 'WAT'),
  'Egypt': createCountryData('Egypt', 'Africa/Cairo', 'EET'),
};

// Helper function to create state timezone data with ISO codes
const createStateData = (name: string, timezone: string, abbreviation: string, stateCode: string, country: string): StateTimezoneData => ({
  name,
  flag: '🌐', // Fallback emoji, will be replaced by SVG
  countryCode: getCountryCodeFromName(country),
  timezone,
  abbreviation,
  stateCode,
  country
});

// State-specific timezone mappings
export const STATE_TIMEZONE_MAP: Record<string, StateTimezoneData> = {
  // US States - Eastern Time
  'NY': createStateData('New York', 'America/New_York', 'EST/EDT', 'NY', 'United States'),
  'NEW YORK': createStateData('New York', 'America/New_York', 'EST/EDT', 'NY', 'United States'),
  'FL': createStateData('Florida', 'America/New_York', 'EST/EDT', 'FL', 'United States'),
  'FLORIDA': createStateData('Florida', 'America/New_York', 'EST/EDT', 'FL', 'United States'),
  'GA': createStateData('Georgia', 'America/New_York', 'EST/EDT', 'GA', 'United States'),
  'GEORGIA': createStateData('Georgia', 'America/New_York', 'EST/EDT', 'GA', 'United States'),
  'NC': createStateData('North Carolina', 'America/New_York', 'EST/EDT', 'NC', 'United States'),
  'NORTH CAROLINA': createStateData('North Carolina', 'America/New_York', 'EST/EDT', 'NC', 'United States'),
  'SC': createStateData('South Carolina', 'America/New_York', 'EST/EDT', 'SC', 'United States'),
  'SOUTH CAROLINA': createStateData('South Carolina', 'America/New_York', 'EST/EDT', 'SC', 'United States'),
  'VA': createStateData('Virginia', 'America/New_York', 'EST/EDT', 'VA', 'United States'),
  'VIRGINIA': createStateData('Virginia', 'America/New_York', 'EST/EDT', 'VA', 'United States'),
  'NJ': createStateData('New Jersey', 'America/New_York', 'EST/EDT', 'NJ', 'United States'),
  'NEW JERSEY': createStateData('New Jersey', 'America/New_York', 'EST/EDT', 'NJ', 'United States'),
  'CT': createStateData('Connecticut', 'America/New_York', 'EST/EDT', 'CT', 'United States'),
  'CONNECTICUT': createStateData('Connecticut', 'America/New_York', 'EST/EDT', 'CT', 'United States'),
  'MA': createStateData('Massachusetts', 'America/New_York', 'EST/EDT', 'MA', 'United States'),
  'MASSACHUSETTS': createStateData('Massachusetts', 'America/New_York', 'EST/EDT', 'MA', 'United States'),

  // US States - Central Time
  'TX': createStateData('Texas', 'America/Chicago', 'CST/CDT', 'TX', 'United States'),
  'TEXAS': createStateData('Texas', 'America/Chicago', 'CST/CDT', 'TX', 'United States'),
  'IL': { name: 'Illinois', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'IL', country: 'United States' },
  'ILLINOIS': { name: 'Illinois', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'IL', country: 'United States' },
  'MO': { name: 'Missouri', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'MO', country: 'United States' },
  'MISSOURI': { name: 'Missouri', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'MO', country: 'United States' },
  'WI': { name: 'Wisconsin', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'WI', country: 'United States' },
  'WISCONSIN': { name: 'Wisconsin', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'WI', country: 'United States' },
  'MN': { name: 'Minnesota', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'MN', country: 'United States' },
  'MINNESOTA': { name: 'Minnesota', flag: '🇺🇸', timezone: 'America/Chicago', abbreviation: 'CST/CDT', stateCode: 'MN', country: 'United States' },

  // US States - Mountain Time
  'CO': { name: 'Colorado', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'CO', country: 'United States' },
  'COLORADO': { name: 'Colorado', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'CO', country: 'United States' },
  'UT': { name: 'Utah', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'UT', country: 'United States' },
  'UTAH': { name: 'Utah', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'UT', country: 'United States' },
  'NM': { name: 'New Mexico', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'NM', country: 'United States' },
  'NEW MEXICO': { name: 'New Mexico', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'NM', country: 'United States' },
  'WY': { name: 'Wyoming', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'WY', country: 'United States' },
  'WYOMING': { name: 'Wyoming', flag: '🇺🇸', timezone: 'America/Denver', abbreviation: 'MST/MDT', stateCode: 'WY', country: 'United States' },
  'AZ': { name: 'Arizona', flag: '🇺🇸', timezone: 'America/Phoenix', abbreviation: 'MST', stateCode: 'AZ', country: 'United States' },
  'ARIZONA': { name: 'Arizona', flag: '🇺🇸', timezone: 'America/Phoenix', abbreviation: 'MST', stateCode: 'AZ', country: 'United States' },

  // US States - Pacific Time
  'CA': { name: 'California', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'CA', country: 'United States' },
  'CALIFORNIA': { name: 'California', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'CA', country: 'United States' },
  'WA': { name: 'Washington', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'WA', country: 'United States' },
  'WASHINGTON': { name: 'Washington', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'WA', country: 'United States' },
  'OR': { name: 'Oregon', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'OR', country: 'United States' },
  'OREGON': { name: 'Oregon', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'OR', country: 'United States' },
  'NV': { name: 'Nevada', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'NV', country: 'United States' },
  'NEVADA': { name: 'Nevada', flag: '🇺🇸', timezone: 'America/Los_Angeles', abbreviation: 'PST/PDT', stateCode: 'NV', country: 'United States' },

  // US States - Alaska and Hawaii
  'AK': { name: 'Alaska', flag: '🇺🇸', timezone: 'America/Anchorage', abbreviation: 'AKST/AKDT', stateCode: 'AK', country: 'United States' },
  'ALASKA': { name: 'Alaska', flag: '🇺🇸', timezone: 'America/Anchorage', abbreviation: 'AKST/AKDT', stateCode: 'AK', country: 'United States' },
  'HI': { name: 'Hawaii', flag: '🇺🇸', timezone: 'Pacific/Honolulu', abbreviation: 'HST', stateCode: 'HI', country: 'United States' },
  'HAWAII': { name: 'Hawaii', flag: '🇺🇸', timezone: 'Pacific/Honolulu', abbreviation: 'HST', stateCode: 'HI', country: 'United States' },

  // Canadian Provinces
  'ON': { name: 'Ontario', flag: '🇨🇦', timezone: 'America/Toronto', abbreviation: 'EST/EDT', stateCode: 'ON', country: 'Canada' },
  'ONTARIO': { name: 'Ontario', flag: '🇨🇦', timezone: 'America/Toronto', abbreviation: 'EST/EDT', stateCode: 'ON', country: 'Canada' },
  'QC': { name: 'Quebec', flag: '🇨🇦', timezone: 'America/Toronto', abbreviation: 'EST/EDT', stateCode: 'QC', country: 'Canada' },
  'QUEBEC': { name: 'Quebec', flag: '🇨🇦', timezone: 'America/Toronto', abbreviation: 'EST/EDT', stateCode: 'QC', country: 'Canada' },
  'BC': { name: 'British Columbia', flag: '🇨🇦', timezone: 'America/Vancouver', abbreviation: 'PST/PDT', stateCode: 'BC', country: 'Canada' },
  'BRITISH COLUMBIA': { name: 'British Columbia', flag: '🇨🇦', timezone: 'America/Vancouver', abbreviation: 'PST/PDT', stateCode: 'BC', country: 'Canada' },
  'AB': { name: 'Alberta', flag: '🇨🇦', timezone: 'America/Edmonton', abbreviation: 'MST/MDT', stateCode: 'AB', country: 'Canada' },
  'ALBERTA': { name: 'Alberta', flag: '🇨🇦', timezone: 'America/Edmonton', abbreviation: 'MST/MDT', stateCode: 'AB', country: 'Canada' },

  // Australian States  
  'NSW': { name: 'New South Wales', flag: '🇦🇺', timezone: 'Australia/Sydney', abbreviation: 'AEST/AEDT', stateCode: 'NSW', country: 'Australia' },
  'NEW SOUTH WALES': { name: 'New South Wales', flag: '🇦🇺', timezone: 'Australia/Sydney', abbreviation: 'AEST/AEDT', stateCode: 'NSW', country: 'Australia' },
  'VIC': { name: 'Victoria', flag: '🇦🇺', timezone: 'Australia/Melbourne', abbreviation: 'AEST/AEDT', stateCode: 'VIC', country: 'Australia' },
  'VICTORIA': { name: 'Victoria', flag: '🇦🇺', timezone: 'Australia/Melbourne', abbreviation: 'AEST/AEDT', stateCode: 'VIC', country: 'Australia' },
  'QLD': { name: 'Queensland', flag: '🇦🇺', timezone: 'Australia/Brisbane', abbreviation: 'AEST', stateCode: 'QLD', country: 'Australia' },
  'QUEENSLAND': { name: 'Queensland', flag: '🇦🇺', timezone: 'Australia/Brisbane', abbreviation: 'AEST', stateCode: 'QLD', country: 'Australia' },
  'WA_AU': { name: 'Western Australia', flag: '🇦🇺', timezone: 'Australia/Perth', abbreviation: 'AWST', stateCode: 'WA', country: 'Australia' },
  'WESTERN AUSTRALIA': { name: 'Western Australia', flag: '🇦🇺', timezone: 'Australia/Perth', abbreviation: 'AWST', stateCode: 'WA', country: 'Australia' },
  'SA': { name: 'South Australia', flag: '🇦🇺', timezone: 'Australia/Adelaide', abbreviation: 'ACST/ACDT', stateCode: 'SA', country: 'Australia' },
  'SOUTH AUSTRALIA': { name: 'South Australia', flag: '🇦🇺', timezone: 'Australia/Adelaide', abbreviation: 'ACST/ACDT', stateCode: 'SA', country: 'Australia' },
};

// State name normalization mapping
export const STATE_NORMALIZATION_MAP: Record<string, string> = {
  'NEW YORK': 'NY',
  'new york': 'NY', 
  'New York': 'NY',
  'CALIFORNIA': 'CA',
  'california': 'CA',
  'California': 'CA',
  'TEXAS': 'TX',
  'texas': 'TX',
  'Texas': 'TX',
  'FLORIDA': 'FL',
  'florida': 'FL',
  'Florida': 'FL',
  'ILLINOIS': 'IL',
  'illinois': 'IL',
  'Illinois': 'IL',
  'GEORGIA': 'GA',
  'georgia': 'GA',
  'Georgia': 'GA',
  'ONTARIO': 'ON',
  'ontario': 'ON',
  'Ontario': 'ON',
  'QUEBEC': 'QC',
  'quebec': 'QC',
  'Quebec': 'QC',
  'BRITISH COLUMBIA': 'BC',
  'british columbia': 'BC',
  'British Columbia': 'BC',
  'ALBERTA': 'AB',
  'alberta': 'AB',
  'Alberta': 'AB',
};

// Default fallback timezone (UTC) with fallback flag
export const DEFAULT_TIMEZONE: CountryTimezoneData = {
  name: 'UTC',
  flag: '🌐',
  countryCode: 'no', // Use Norway flag as fallback
  timezone: 'UTC',
  abbreviation: 'UTC'
};

export const getCountryTimezone = (countryName?: string | null): CountryTimezoneData => {
  if (!countryName) {
    return DEFAULT_TIMEZONE;
  }
  
  // Try exact match first
  if (COUNTRY_TIMEZONE_MAP[countryName]) {
    return COUNTRY_TIMEZONE_MAP[countryName];
  }
  
  // Try case-insensitive match
  const normalizedCountry = countryName.toLowerCase();
  for (const [key, value] of Object.entries(COUNTRY_TIMEZONE_MAP)) {
    if (key.toLowerCase() === normalizedCountry) {
      return value;
    }
  }
  
  // Try partial match
  for (const [key, value] of Object.entries(COUNTRY_TIMEZONE_MAP)) {
    if (key.toLowerCase().includes(normalizedCountry) || normalizedCountry.includes(key.toLowerCase())) {
      return value;
    }
  }
  
  // Final fallback: try to get country code directly from name mapping
  const countryCode = getCountryCodeFromName(countryName);
  if (countryCode) {
    console.log(`Using direct country code mapping for: ${countryName} -> ${countryCode}`);
    return {
      name: countryName,
      flag: '🌐',
      countryCode: countryCode,
      timezone: 'UTC',
      abbreviation: 'UTC'
    };
  }
  
  return DEFAULT_TIMEZONE;
};

export const hasCountryTimezone = (countryName?: string | null): boolean => {
  if (!countryName) return false;
  const input = countryName.trim();
  if (!input) return false;

  // Exact match
  if (COUNTRY_TIMEZONE_MAP[input]) return true;

  // Case-insensitive match
  const normalized = input.toLowerCase();
  for (const key of Object.keys(COUNTRY_TIMEZONE_MAP)) {
    if (key.toLowerCase() === normalized) return true;
  }

  // Partial match
  for (const key of Object.keys(COUNTRY_TIMEZONE_MAP)) {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes(normalized) || normalized.includes(lowerKey)) return true;
  }
  return false;
};

// Normalize state name to standard abbreviation
export const normalizeStateName = (input?: string | null): string | null => {
  if (!input || input.trim() === '') return null;
  
  const trimmed = input.trim();
  
  // Check if it's already a normalized state code
  if (STATE_TIMEZONE_MAP[trimmed.toUpperCase()]) {
    return trimmed.toUpperCase();
  }
  
  // Check normalization map
  if (STATE_NORMALIZATION_MAP[trimmed]) {
    return STATE_NORMALIZATION_MAP[trimmed];
  }
  
  // Try case variations
  const variations = [trimmed, trimmed.toLowerCase(), trimmed.toUpperCase()];
  for (const variation of variations) {
    if (STATE_NORMALIZATION_MAP[variation]) {
      return STATE_NORMALIZATION_MAP[variation];
    }
  }
  
  return null;
};

// Get state-specific timezone data
export const getStateTimezone = (state?: string | null, country?: string | null): StateTimezoneData | null => {
  if (!state) return null;
  
  const normalizedState = normalizeStateName(state);
  if (!normalizedState) return null;
  
  const stateData = STATE_TIMEZONE_MAP[normalizedState];
  if (!stateData) return null;
  
  // Verify country matches if provided
  if (country && !country.toLowerCase().includes(stateData.country.toLowerCase()) && 
      !stateData.country.toLowerCase().includes(country.toLowerCase())) {
    return null;
  }
  
  return stateData;
};

// Check if state timezone data exists
export const hasStateTimezone = (state?: string | null, country?: string | null): boolean => {
  return getStateTimezone(state, country) !== null;
};

// Enhanced timezone resolution - prioritizes state over country
export const getEnhancedTimezone = (country?: string | null, state?: string | null): CountryTimezoneData => {
  // First try to get state-specific timezone
  const stateTimezone = getStateTimezone(state, country);
  if (stateTimezone) {
    return stateTimezone;
  }
  
  // Fall back to country timezone
  const countryTimezone = getCountryTimezone(country);
  return countryTimezone;
};

export const isBusinessHours = (timezone: string): boolean => {
  try {
    const now = new Date();
    const timeInTimezone = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false
    }).format(now);
    
    const hour = parseInt(timeInTimezone, 10);
    return hour >= 9 && hour < 18; // 9 AM to 6 PM
  } catch (error) {
    console.error('Error checking business hours:', error);
    return false;
  }
};
